import { LANGUAGE_SETTINGS, IMAGE_PATHS, APP_VERSION } from '/settings.js';
import { setLanguage, getCurrentLanguage } from '../translations/translations.js';

/**
 * Adds version query parameter to URL for cache-busting
 * Matches the logic from static-assets-cache.js to ensure URLs match preloaded assets
 */
function addVersionToUrl(url) {
    if (!APP_VERSION) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${APP_VERSION}`;
}

/**
 * Creates a DOM element for the language switcher
 * @param {Object} [options={}]
 * @param {boolean} [options.preventReload=false] - If true, changing language won't reload page
 * @returns {HTMLElement|null} The language switcher element, or null if no flags are enabled
 */
export function createLanguageSwitcher(options = {}) {
    const { preventReload = false, onBeforeReload } = options;
    const showFlags = LANGUAGE_SETTINGS.SHOW_LANGUAGE_FLAGS || {};

    // Check if we should show anything
    if (showFlags.es === false && showFlags.en === false) {
        return null;
    }

    const switcher = document.createElement('div');
    switcher.className = 'language-switcher';

    // Create Spanish flag button (only if enabled)
    if (showFlags.es !== false) {
        const esButton = createButton('es', 'Español', 'es.webp', preventReload, onBeforeReload);
        switcher.appendChild(esButton);
    }

    // Create English flag button (only if enabled)
    if (showFlags.en !== false) {
        const enButton = createButton('en', 'English', 'en.webp', preventReload, onBeforeReload);
        switcher.appendChild(enButton);
    }

    // Set initial active state
    const currentLang = getCurrentLanguage();
    updateActiveState(switcher, currentLang);

    // Listen for global changes to update state even if triggered elsewhere
    window.addEventListener('languageChanged', (e) => {
        updateActiveState(switcher, e.detail.language);
    });

    return switcher;
}

function createButton(langCode, title, iconFile, preventReload, onBeforeReload) {
    const button = document.createElement('button');
    button.className = 'language-btn';
    button.setAttribute('data-lang', langCode);
    button.title = title;

    const img = document.createElement('img');
    img.src = addVersionToUrl(`${IMAGE_PATHS.FLAGS}${iconFile}`);
    img.alt = title;

    button.appendChild(img);
    button.addEventListener('click', async (e) => {
        e.stopPropagation(); // Prevent menu closing if needed
        // If a hook is provided and we're about to reload, call it
        if (!preventReload && typeof onBeforeReload === 'function') {
            onBeforeReload();
        }
        await setLanguage(langCode, !preventReload);
    });

    return button;
}

/**
 * Updates the active state of buttons within a specific switcher or globally
 * @param {HTMLElement} [container] - Specific container to update. If null, updates all .language-btn in document
 * @param {string} activeLang - The active language code
 */
export function updateActiveState(container, activeLang) {
    const selector = container ? container.querySelectorAll('.language-btn') : document.querySelectorAll('.language-btn');
    selector.forEach(button => {
        if (button.getAttribute('data-lang') === activeLang) {
            button.classList.add('active');
        } else {
            button.classList.remove('active');
        }
    });
}

